/**
 * 图片滤镜插件自定义历史
 *
 * 从 ImageLayer 迁入滤镜提交的 undo/redo 与协同差异处理，使 MainLayer 和 ImageLayer 共用同一实现。
 */
import { Framework, type IHistoryItem } from '@/framework';
import { applyDifferentProps, getDifferentProps } from '@/utils/layer-kit';
import { IMAGE_FILTER_HISTORY_TYPE, type IImageFilterHistoryData } from './types';

/** 注册图片滤镜提交历史。 */
export class ImageFilterHistory extends Framework {
    /**
     * 创建并注册滤镜历史
     *
     * @param identity - 编辑器实例标识
     * 副作用：向当前编辑器历史服务注册 applyFilter 类型。
     */
    constructor(identity: string) {
        super(identity);
        this.framework.service.history.register(IMAGE_FILTER_HISTORY_TYPE, {
            owner: 'layer/image/image-filter',
            collaboration: 'supported',
            isLikeDefault: true,
            onRedoUndo: (historyItem: IHistoryItem<IImageFilterHistoryData>, isRedo: boolean) => {
                if (historyItem.isDirty) return;
                const { oldLayer, newLayer, diffInfo } = historyItem.data.data.item;
                if (diffInfo) {
                    Object.keys(diffInfo).forEach((layerId) => {
                        const differentProps = diffInfo[layerId];
                        const layer = this.framework.service.flatLayer.getLayer(layerId, true);
                        if (!layer) return;
                        applyDifferentProps(layer, differentProps);
                        this.framework.service.frame.addOrUpdateLayer(layer);
                    });
                } else {
                    this.framework.service.frame.addOrUpdateLayer(isRedo ? newLayer : oldLayer);
                }
                this.framework.service.frame.setActiveLayer(oldLayer.id);
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IImageFilterHistoryData>, isRedo, isUndo, store) => {
                const { oldLayer, newLayer } = historyItem.data.data.item;
                if (isUndo) {
                    const latestLayer = this.framework.service.flatLayer.getLayer(oldLayer.id, true);
                    if (!latestLayer) historyItem.isDirty = true;
                    historyItem.data.data.item.diffInfo = getDifferentProps(
                        oldLayer,
                        newLayer,
                        undefined,
                        new Set(['parentId']),
                    );
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                        oldLayer,
                        newLayer: latestLayer,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (historyItem.isDirty) return;
                    const latestLayer = this.framework.service.flatLayer.getLayer(beforeData.newLayer.id);
                    if (!latestLayer) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.data.data.item.diffInfo = getDifferentProps(
                        beforeData.newLayer,
                        beforeData.oldLayer,
                        undefined,
                        new Set(['parentId']),
                    );
                    historyItem.data.data.item.newLayer = beforeData.newLayer;
                    historyItem.data.data.item.oldLayer = beforeData.oldLayer;
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IImageFilterHistoryData>) => {
                const { oldLayer, newLayer } = historyItem.data.data.item;
                return { message: [getDifferentProps(newLayer, oldLayer), oldLayer.id] };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IImageFilterHistoryData>) => {
                const { oldLayer, diffInfo } = historyItem.data.data.item;
                return { message: [diffInfo, oldLayer.id] };
            },
            onMessage: (message: [Record<string, object>, string]) => {
                const [diffInfo, layerId] = message;
                if (!diffInfo) return;
                const targetLayer = this.framework.service.flatLayer.getLayer(layerId, true);
                if (!targetLayer) return;
                Object.keys(diffInfo).forEach((changedLayerId) => {
                    const layer = this.framework.service.flatLayer.getLayer(changedLayerId, true);
                    if (!layer) return;
                    applyDifferentProps(layer, diffInfo[changedLayerId]);
                    this.framework.service.frame.addOrUpdateLayer(layer);
                    this.framework.service.frame.tryRefreshActiveLayer(changedLayerId);
                });
            },
        });
    }
}
